import { useEffect, useState } from 'react';
import {
  Page,
  Card,
  BlockStack,
  Text,
  InlineGrid,
  InlineStack,
  Badge,
  Button,
  Banner,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../providers/ApiProvider';
import { DEFAULT_POPUP_DESIGN } from './widgets/popupDefaults';
import { PopupPreview } from './widgets/PopupPreview';
import { DEFAULT_MILESTONE_DESIGN } from './widgets/milestoneDefaults';
import { MilestonePreview } from './widgets/MilestonePreview';
import { DEFAULT_CONGRATS_DESIGN } from './widgets/congratsDefaults';
import { CongratsPreview } from './widgets/CongratsPreview';
import { DEFAULT_PROMO_DESIGN } from './widgets/promoDefaults';
import { PromoPreview } from './widgets/PromoPreview';
import { t } from '../i18n';

interface WidgetOption {
  id: string;
  title: string;
  description: string;
  available: boolean;
  route?: string;
  type?: string;
}

const OPTIONS: WidgetOption[] = [
  {
    id: 'popup',
    title: 'Pop-up',
    description:
      'Let customers choose their preferred rewards when they qualify',
    available: true,
    route: '/widgets/popup',
    type: 'POPUP',
  },
  {
    id: 'milestone',
    title: 'Milestone progress',
    description:
      'Show tiered cart-goal progress on product pages with See details tiers',
    available: true,
    route: '/widgets/milestone',
    type: 'PROGRESS_BAR',
  },
  {
    id: 'congrats',
    title: 'Cart Success Notification Bar',
    description:
      '“Gift Locked In 🎁” toast when a free gift is added — auto-closes with countdown border',
    available: true,
    route: '/widgets/congrats',
    type: 'STICKY_BAR',
  },
  {
    id: 'promo',
    title: 'Product Page Promotion Widget',
    description:
      'Gift carousel on the product page for Spend, Add X, and BXGY / BOGO campaigns',
    available: true,
    route: '/widgets/promo',
    type: 'PRODUCT_WIDGET',
  },
];

export function WidgetsPage() {
  const api = useApi();
  const navigate = useNavigate();
  const [enabledTypes, setEnabledTypes] = useState<Set<string>>(new Set());

  useEffect(() => {
    api
      .get<Array<{ type: string; isEnabled?: boolean }>>('/widgets')
      .then((list) => {
        const next = new Set<string>();
        (list || []).forEach((w) => {
          if (w.isEnabled !== false) next.add(w.type);
        });
        setEnabledTypes(next);
      })
      .catch(() => setEnabledTypes(new Set()));
  }, [api]);

  return (
    <Page
      title={t("Widgets")}
      subtitle={t("Choose how free gifts and milestones appear on your storefront")}
    >
      <BlockStack gap="400">
        <Banner tone="info">
          <p>
            <strong>{t("Pop-up")}</strong>, <strong>{t("Milestone progress")}</strong>,{' '}
            <strong>{t("Cart Success Notification Bar")}</strong>{t(", and")}{' '}
            <strong>{t("Product Page Promotion Widget")}</strong> {t("are available.\n            Customize each once — campaigns reuse these designs store-wide.")}
          </p>
        </Banner>

        <InlineGrid columns={{ xs: 1, sm: 2 }} gap="400">
          {OPTIONS.map((opt) => {
            const inUse =
              opt.available &&
              !!opt.type &&
              enabledTypes.has(opt.type);
            return (
              <Card key={opt.id} padding="0">
                <div style={{ overflow: 'hidden' }}>
                  {/* Mini preview strip */}
                  <div
                    style={{
                      height: 160,
                      overflow: 'hidden',
                      background: '#F0F2F4',
                      pointerEvents: 'none',
                      opacity: opt.available ? 1 : 0.75,
                    }}
                  >
                    {opt.id === 'popup' ? (
                      <div
                        style={{
                          transform: 'scale(0.52)',
                          transformOrigin: 'top center',
                          marginTop: -8,
                        }}
                      >
                        <PopupPreview
                          design={DEFAULT_POPUP_DESIGN}
                          mode="desktop"
                          selectedCount={1}
                        />
                      </div>
                    ) : opt.id === 'milestone' ? (
                      <div
                        style={{
                          transform: 'scale(0.58)',
                          transformOrigin: 'top center',
                          marginTop: 4,
                          padding: '0 12px',
                        }}
                      >
                        <MilestonePreview
                          design={DEFAULT_MILESTONE_DESIGN}
                          mode="desktop"
                          progressPercent={28}
                        />
                      </div>
                    ) : opt.id === 'promo' ? (
                      <div
                        style={{
                          transform: 'scale(0.72)',
                          transformOrigin: 'top center',
                          marginTop: 4,
                        }}
                      >
                        <PromoPreview
                          design={DEFAULT_PROMO_DESIGN}
                          mode="desktop"
                          variant="card"
                        />
                      </div>
                    ) : (
                      <CongratsPreview
                        design={DEFAULT_CONGRATS_DESIGN}
                        mode="desktop"
                        progressPercent={65}
                        variant="card"
                      />
                    )}
                  </div>

                  <div style={{ padding: 16 }}>
                    <BlockStack gap="200">
                      <InlineStack align="space-between" blockAlign="center">
                        <Text as="h2" variant="headingMd">
                          {opt.title}
                        </Text>
                        <Badge tone={inUse ? 'success' : undefined}>
                          {opt.available
                            ? inUse
                              ? 'In use'
                              : 'Ready'
                            : 'Not use'}
                        </Badge>
                      </InlineStack>
                      <Text as="p" tone="subdued" variant="bodySm">
                        {opt.description}
                      </Text>
                      <InlineStack gap="200">
                        <Button
                          variant={opt.available ? 'primary' : undefined}
                          disabled={!opt.available}
                          onClick={() => opt.route && navigate(opt.route)}
                        >
                          {t("Set up")}
                        </Button>
                        <Button
                          variant="plain"
                          disabled={!opt.available}
                          onClick={() =>
                            opt.route && navigate(`${opt.route}?preview=1`)
                          }
                        >
                          {t("Preview")}
                        </Button>
                      </InlineStack>
                    </BlockStack>
                  </div>
                </div>
              </Card>
            );
          })}
        </InlineGrid>
      </BlockStack>
    </Page>
  );
}
